/**
 * Features page screenshots — files live in assets/features/.
 * Naming: `{org|citizen}_{slide_id}.png` (hyphens in slide id → underscores).
 *
 * A slide can show several screenshots. Extra shots reuse the slide filename as
 * a prefix and add their own suffix, e.g. for the "campaigns" slide:
 *   org_campaigns.png            → shown first
 *   org_campaigns_bill_link.png  → shown after base, sorted by suffix
 *   org_campaigns_2_schedule.png → number the suffix to control order
 *
 * Because extras are matched by prefix, a slide id must never be the prefix of
 * another slide id in the same audience.
 *
 * Missing files fall back to `_placeholder.png`.
 */

const featureImageContext = require.context(
  "../assets/features",
  false,
  /\.png$/
);

const PLACEHOLDER_KEY = "./_placeholder.png";

const featureImages = Object.fromEntries(
  featureImageContext.keys().map((key) => [key.replace(/^\.\//, ""), featureImageContext(key)])
);

const placeholderSrc = featureImages[PLACEHOLDER_KEY.replace(/^\.\//, "")] ?? null;

function slideIdToSlug(slideId) {
  return String(slideId).replace(/-/g, "_");
}

function compareFilenames(a, b) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

/**
 * All screenshots for a slide, base image first.
 *
 * @param {"org"|"citizen"} audience
 * @param {string} slideId — matches PlansSection item `id` (e.g. "follow-up")
 * @returns {string[]}
 */
export function getFeaturePreviews(audience, slideId) {
  const base = `${audience}_${slideIdToSlug(slideId)}`;
  const extras = Object.keys(featureImages)
    .filter((filename) => filename.startsWith(`${base}_`))
    .sort(compareFilenames)
    .map((filename) => featureImages[filename]);

  const baseImage = featureImages[`${base}.png`];
  const previews = baseImage ? [baseImage, ...extras] : extras;

  if (previews.length > 0) return previews;
  return placeholderSrc ? [placeholderSrc] : [];
}

/**
 * @param {"org"|"citizen"} audience
 * @param {string} slideId — matches PlansSection item `id` (e.g. "follow-up")
 */
export function getFeaturePreview(audience, slideId) {
  return getFeaturePreviews(audience, slideId)[0] ?? null;
}

/** Maps FeatureCarousel `idPrefix` to features folder audience prefix. */
export function audienceFromIdPrefix(idPrefix) {
  return idPrefix === "organizer" ? "org" : "citizen";
}
